import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileText, Search, ShoppingBag, Building2, Award, Zap, DollarSign, HeadphonesIcon, CheckCircle, Star, ArrowRight, Check } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import ServiceSlider from '../components/ServiceSlider';
import WhyChooseUsSlider from '../components/WhyChooseUsSlider';
import HowItWorksSlider from '../components/HowItWorksSlider';
import PopupButton from '../components/PopupButton';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { services, pricingPlans, faqs, howItWorks, whyChooseUs } from '../data/mock';
import SEO from '../components/SEO';

const iconMap = {
  Shield,
  FileText,
  Search,
  ShoppingBag,
  Building2,
  Award,
  Zap,
  DollarSign,
  HeadphonesIcon,
  CheckCircle
};

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Assess Trademark | Protect Your Brand with Trademark Registration"
        description="Assess Trademark offers trusted trademark registration, copyright registration, trademark search, Amazon Brand Registry, and company formation services for businesses across the USA."
      />
      {/* Hero Section */}
      <section className="relative bg-primary py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                  File Your
                  <span className="block  text-secondary">Trademark</span>
                </h1>
                <p className="text-lg md:text-xl text-white/90 leading-relaxed">
                  <span className="font-bold text-white">Protect Your Identity. Block Copycats.</span> Get complete legal security for your business name, logo, and brand identity.
                  <span className="block mt-3 font-bold text-white text-xl md:text-2xl">Starts at $45 • 24-Hour Support • Guaranteed Approval</span>
                </p>
              </div>
              <div className="flex flex-wrap gap-4 mt-8">
                <PopupButton size="lg" className="bg-secondary hover:bg-secondary/90 text-white px-8 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
                  Register My Trademark
                  <ArrowRight className="ml-2 w-5 h-5" />
                </PopupButton>
                <Button size="lg" variant="outline" className="js-chat border-2 border-white text-white hover:bg-white/10 shadow-lg hover:shadow-xl transition-all duration-300">
                  Live Chat
                </Button>
              </div>
              
              {/* Trust badges */}
              <div className="flex flex-wrap gap-6 items-center pt-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-white/90">Live Support Chat</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-white/90">Zero Hidden Charges</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-white/90">24/7 Assistance</span>
                </div>
              </div>
            </div>
            
            {/* Enhanced Form with Glow Effect */}
            <div className="relative">
              <div className="absolute inset-0 bg-primary rounded-3xl blur-2xl opacity-20 animate-pulse"></div>
              <div className="relative bg-white p-8 md:p-10 rounded-3xl shadow-2xl border border-primary/20">
                <div className="mb-6 text-center">
                  <h3 className="text-3xl font-bold text-primary mb-3">
                    Start Your Trademark Filing
                  </h3>
                  <p className="text-gray-600 text-lg">
                    Secure your brand today with our quick and trusted trademark services.
                  </p>
                </div>
                <ContactForm variant="compact" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-primary/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Simple 3 Step Process</h2>
            <p className="text-lg text-gray-600">Fast, clear, and fully transparent process</p>
          </div>
          <HowItWorksSlider items={howItWorks} />
        </div>
      </section>

      {/* Why Choose Us Section 1 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Why Assess Trademark Is the Right Choice
              </h2>
              <p className="text-lg text-gray-600">
                At Assess Trademark, we make the trademark registration process simple, affordable, and fully managed by experts. Whether you are launching a startup or protecting an established business, we deliver complete support from filing to approval, ensuring accuracy, speed, and full USPTO compliance.
              </p>
              <p className="text-lg text-gray-600">
                What makes us different is our focus on personalized assistance and clear pricing. With a smooth workflow, responsive support, and dedication to your success, Assess Trademark ensures your brand protection without complications.
              </p>

                         <PopupButton size="lg" className="bg-secondary hover:bg-secondary/90 text-white px-8 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
                   Register Your Brand
                </PopupButton>

            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-2xl flex items-center justify-center bg-white p-8">
                <img 
                  src="/assets/img/why-us-home.jpg" 
                  alt="Flat design illustration of business professionals shaking hands - partnership and agreement"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">Why Choose Assess Trademark</h2>
          <WhyChooseUsSlider items={whyChooseUs.slice(0, 3)} />
          
          <div className="grid lg:grid-cols-2 gap-12 items-center py-20">
            <div className="relative">
              <div className="w-full h-[500px] bg-primary/20 rounded-2xl flex items-center justify-center p-4">
                <img 
                  src="/assets/img/Discover-More-Reasons-home.svg" 
                  alt="Business Growth and Trust Illustration"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            <div className="space-y-6 pt-8">
              <h3 className="text-3xl font-bold text-gray-900">
                Trust Built on Proven Results That Matter Most
              </h3>
              <p className="text-lg text-gray-600">
                Trust is not only about results, it is built through consistency, communication, and care. Businesses across the U.S. choose us because we combine legal accuracy with personal attention, ensuring every application is handled with urgency and detail.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-12 h-12 bg-secondary rounded-full flex items-center justify-center">
                    <HeadphonesIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Personal Support</h4>
                    <p className="text-gray-600">Get expert guidance at every stage with responsive assistance.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-12 h-12 bg-secondary rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">100% USPTO Compliance</h4>
                    <p className="text-gray-600">We ensure your application meets all legal standards accurately.</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-lg text-gray-600">Protect your brand and scale your business with our reliable services</p>
          </div>
          <ServiceSlider services={services} />
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Start with Affordable Trademark Plans
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan) => (
              <Card key={plan.id} className={`relative flex flex-col h-full hover:shadow-2xl transition-all duration-300 ${plan.recommended ? 'ring-2 ring-primary scale-105' : ''}`}>
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-secondary text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Recommended
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-6">
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">+Federal Filing Fee</p>
                  <CardDescription className="mt-4">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="flex-grow overflow-y-auto max-h-64 pb-4">
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <Check className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="mt-auto pt-4">
                  <PopupButton className="w-full bg-secondary hover:bg-secondary/90 text-white">
                    Register Your Trademark
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </PopupButton>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Strong Protection for Your Brand
          </h2>
          <p className="text-lg md:text-xl mb-8 opacity-90">
            Begin your trademark registration today with expert assistance and complete confidence.
          </p>
          <div className="flex justify-center gap-4">
            <PopupButton size="lg" variant="secondary" className="bg-white text-primary hover:bg-gray-100">
              Register Your Brand
            </PopupButton>
            <Button size="lg" variant="outline" className="js-chat border-white text-white hover:bg-white hover:text-primary">
              Live Support Chat
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-center text-gray-600 mb-12">
            Find answers to the most common questions clients ask before starting trademark registration.
          </p>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                <AccordionTrigger className="text-left font-semibold">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-gray-600">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default Home;
